/**
 * Debate topology scene — Forum Campus.
 *
 * Campus layout:
 *   - Judge/moderator building (4x4 booth) centered at top — small facilitator
 *   - Debater buildings (5x5 stages) in a row below — big performers
 *   - Stone paths from judge to each debater, and between adjacent debaters
 *
 * Scales: more debaters = wider row.
 */

import type { SceneLayout, TopologyInfo, BuildingFootprint, CampusDef } from "./SceneLayout.js";
import { campusToLayout, routePath } from "./SceneLayout.js";
import { getFurnitureForBuilding } from "./FurniturePresets.js";

const JUDGE_SIZE = 4;    // Moderator: small booth (facilitator)
const DEBATER_SIZE = 5;  // Debaters: big stage (main performers)
const GAP = 4;

/** Detect debate pattern: hub-and-spoke where one central node connects to many others. */
export function isDebatePattern(topo: TopologyInfo): boolean {
  if (topo.agents.length < 3) return false;

  // Count incoming/outgoing edges per agent (any edge type, not just "reports-to")
  const inCount = new Map<string, number>();
  const outCount = new Map<string, number>();
  for (const e of topo.edges) {
    inCount.set(e.to, (inCount.get(e.to) ?? 0) + 1);
    outCount.set(e.from, (outCount.get(e.from) ?? 0) + 1);
  }

  const hasHub = [...inCount.values()].some((c) => c >= 2);
  const hasPeers = topo.edges.some((e) => e.type === "peers-with" || e.bidirectional);

  if (hasHub) {
    const hasFanOut = [...outCount.values()].some((c) => c >= 2);
    if (!hasFanOut) return true; // Pure fan-in → debate

    // Both fan-in and fan-out: check if same node is the hub for both (debate)
    // or different nodes (map-reduce)
    let hubInId = "";
    let maxIn = 0;
    for (const [id, count] of inCount) {
      if (count > maxIn) { maxIn = count; hubInId = id; }
    }
    let hubOutId = "";
    let maxOut = 0;
    for (const [id, count] of outCount) {
      if (count > maxOut) { maxOut = count; hubOutId = id; }
    }

    // Same node as hub for both in and out → debate (bidirectional hub)
    if (hubInId === hubOutId) return true;

    // Different hub nodes → map-reduce pattern, not debate
    return false;
  }

  // No hub but has peer connections → debate-like
  return hasPeers;
}

export function debateLayout(topo: TopologyInfo): SceneLayout {
  const buildings: BuildingFootprint[] = [];

  // Find judge/moderator: agent with most incoming edges (any type)
  const inCount = new Map<string, number>();
  for (const e of topo.edges) {
    inCount.set(e.to, (inCount.get(e.to) ?? 0) + 1);
  }

  let judgeId = "";
  let maxIn = 0;
  for (const [id, count] of inCount) {
    if (count > maxIn) {
      maxIn = count;
      judgeId = id;
    }
  }
  if (!judgeId) {
    judgeId = topo.exitPoints?.[0] ?? topo.agents[topo.agents.length - 1]?.id ?? "";
  }

  // Find proposer: entry point that isn't the judge
  const proposerId = topo.entryPoint !== judgeId
    ? topo.entryPoint ?? topo.agents.find((a) => a.id !== judgeId)?.id ?? ""
    : topo.agents.find((a) => a.id !== judgeId)?.id ?? "";

  const critics = topo.agents.filter((a) => a.id !== judgeId && a.id !== proposerId);
  const allDebaters = [proposerId, ...critics.map((c) => c.id)];
  const debaterCount = allDebaters.length;

  // Center debater row
  const debaterTotalWidth = debaterCount * DEBATER_SIZE + (debaterCount - 1) * GAP;
  const debaterStartCol = -Math.floor(debaterTotalWidth / 2);
  const debaterRow = JUDGE_SIZE + GAP;

  // Judge/moderator building (small booth, centered at top)
  const judgeCol = -Math.floor(JUDGE_SIZE / 2);
  const judgeBuilding: BuildingFootprint = {
    id: "judge",
    col: judgeCol,
    row: 0,
    width: JUDGE_SIZE,
    height: JUDGE_SIZE,
    floorType: "stage",
    agents: [{
      agentId: judgeId,
      localCol: 2,
      localRow: 2,
      deskType: "small",
      isEntryPoint: judgeId === topo.entryPoint,
    }],
    furniture: getFurnitureForBuilding("hub", "forum", judgeCol, 0, JUDGE_SIZE, JUDGE_SIZE),
  };
  buildings.push(judgeBuilding);

  // Debater buildings (big stages, row below judge)
  const debaterBuildings: BuildingFootprint[] = [];
  for (let i = 0; i < debaterCount; i++) {
    const bCol = debaterStartCol + i * (DEBATER_SIZE + GAP);
    const b: BuildingFootprint = {
      id: `debater-${i}`,
      col: bCol,
      row: debaterRow,
      width: DEBATER_SIZE,
      height: DEBATER_SIZE,
      floorType: "stage",
      agents: [{
        agentId: allDebaters[i],
        localCol: 2,
        localRow: 2,
        deskType: "large",
        isEntryPoint: allDebaters[i] === topo.entryPoint,
      }],
      furniture: getFurnitureForBuilding("worker", "forum", bCol, debaterRow, DEBATER_SIZE, DEBATER_SIZE),
    };
    buildings.push(b);
    debaterBuildings.push(b);
  }

  // Route paths: judge → each debater, and between adjacent debaters
  const pathCells: Array<{ col: number; row: number }> = [];
  for (const db of debaterBuildings) {
    pathCells.push(...routePath(judgeBuilding, db));
  }
  for (let i = 0; i < debaterBuildings.length - 1; i++) {
    pathCells.push(...routePath(debaterBuildings[i], debaterBuildings[i + 1]));
  }

  const campus: CampusDef = {
    buildings,
    pathCells,
    decorations: [],
  };

  return campusToLayout(campus);
}
